import {useContext, useEffect, useState} from 'react';
import {API_URL} from '../../../constants';
import {securedFetchRequest} from '@bidmii/common/lib/util/FetchRequest';
import {Box, Grid, RadioGroup, Slide, Stack, Typography} from '@mui/material';
import * as Sentry from '@sentry/react';
import {useRecoilValue} from 'recoil';
import {userState} from '../../../recoil/user/userAtom';
import {fireScreenView, SCREEN_NAMES} from '../../../util/Analytics';
import {theme} from '../../../Theme';
import BidmiiRadio from '../../../common/StyledComponents/BidmiiRadio';
import PromoteProjectDialog from '../../ListViews/components/DetailDialogs/components/PromoteProjectDialog';
import {ErrorOutline} from '@mui/icons-material';
import BidmiiCheckbox from '../../../common/StyledComponents/BidmiiCheckbox';
import {makeStyles} from '@mui/styles';
import {NewProjectContext} from '../utils/NewProjectContext';
import ActionButtons from '../components/ActionButtons';

interface IProps {
  projectSubmitCallback: any;
  changeStepHandler: (number: number) => void;
  forward: boolean;
}

const useStyles = makeStyles(() => ({
  root: {
    height: '100vh',

    '& .MuiFormControlLabel-root': {
      marginLeft: -7,
      marginBottom: -10,

      '& .MuiTypography-body1': {
        fontSize: '17px !important',
      },
    },

    '& .MuiFormControlLabel-label': {
      fontSize: 12,
    },

    '& .MuiInput-underline.Mui-error': {
      '&:after': {
        borderBottomColor: theme.palette.primary.main,
      },
    },
  },
  promotionBox: {
    marginTop: 8,
    padding: 12,
    borderRadius: 10,
    border: `1px solid ${theme.palette.gray.border}`,
  },
}));

const PublishAndPromoteStep = ({
  projectSubmitCallback,
  changeStepHandler,
  forward,
}: IProps) => {
  useEffect(() => {
    fireScreenView(SCREEN_NAMES.NEW_PROJECT_WIZARD_STEP_THREE);
  }, []);

  const {project, setProject} = useContext(NewProjectContext);

  const imagesRequired =
    project.attachmentReferences.filter(
      (ref: any) => ref.key === 'before_image',
    ).length === 0;

  const classes = useStyles();
  const user = useRecoilValue(userState);
  const [loading, setLoading] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [saveType, setSaveType] = useState(
    imagesRequired ? 'draft' : 'publish',
  );
  const [willAcceptCalls, setWillAcceptCalls] = useState(
    imagesRequired ? 'false' : 'true',
  );

  const [promotionType, setPromotionType] = useState<string | null>('free');
  const [promotionDialogOpen, setPromotionDialogOpen] = useState(false);

  const handleFinish = () => {
    if (promotionType && promotionType === 'promoted') {
      setPromotionDialogOpen(true);
    } else {
      handleProjectSubmit();
    }
  };

  const handleProjectSubmit = () => {
    const body = {
      state: saveType === 'publish' ? 'OPEN' : 'DRAFT',
      approvedForCalls: willAcceptCalls,
    };

    const inviteUserId = sessionStorage.getItem('inviteUserId');
    if (inviteUserId) {
      inviteContractorToProject(body, inviteUserId);
    } else {
      sendData(body);
    }
  };

  const sendData = (rfp: $TSFixMe) => {
    setSubmitDisabled(true);

    setLoading(true);
    securedFetchRequest(`${API_URL}/rfps/${project.uid}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(rfp),
    })
      .then((res) => res.json())
      .then((res) => {
        setLoading(false);
        setProject(res);
        projectSubmitCallback(res.state);
      })
      .catch((err) => {
        process.env.NODE_ENV === 'development' && console.error(err);

        Sentry.withScope(function (scope) {
          scope.setUser({
            uid: user?.uid,
            firstName: user?.firstName,
          });
          scope.setTag('section', 'create_user_step_2');
          Sentry.captureException(err);
        });
      });
  };

  const inviteContractorToProject = (body: any, inviteUserId: string) => {
    const inviteBody = {
      userIds: [inviteUserId],
      rfpIds: [project.id],
    };
    securedFetchRequest(`${API_URL}/rfps/invite`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(inviteBody),
    }).then(() => {
      sessionStorage.removeItem('inviteUserId');
      sendData(body);
    });
  };

  return (
    <>
      <Slide in={true} direction={forward ? 'left' : 'right'} timeout={400}>
        <div style={{width: '100%', maxWidth: 450}} id="scrollDiv">
          <form noValidate autoComplete="off">
            <Typography variant="h6" fontSize={22}>
              Publish
            </Typography>
            <Box my={1}>
              <Typography variant="subtitle1" fontSize={15} lineHeight={1.4}>
                No worries - there’s no commitment yet. If you decide to award a
                bid, you’ll be prompted to pay the amount quoted, and place your
                money in trust while the work is underway.
              </Typography>
            </Box>

            {imagesRequired && (
              <Box
                mb={1}
                mx={-3}
                py={1.2}
                px={3}
                style={{backgroundColor: theme.palette.red.light}}>
                <Grid container alignItems="center">
                  <ErrorOutline style={{color: theme.palette.red.main}} />
                  <Typography
                    variant="subtitle2"
                    style={{maxWidth: '80%', lineHeight: 1.3, marginLeft: 8}}>
                    Images are required before this project can be published.
                  </Typography>
                </Grid>
              </Box>
            )}

            <Stack gap={3}>
              <RadioGroup
                aria-label="deadline"
                name="deadline"
                value={saveType}
                onChange={(e) => setSaveType(e.target.value)}>
                <BidmiiRadio
                  state={saveType}
                  value="publish"
                  label="Post now (start getting bids)"
                  disabled={imagesRequired}
                />
                <BidmiiRadio
                  state={saveType}
                  value="draft"
                  label="Save as draft"
                />
              </RadioGroup>

              <div>
                <Typography variant="subtitle1" fontSize={15} lineHeight={1.4}>
                  Would you like to get calls from contractors?
                </Typography>
                <Typography variant="caption" fontSize={13}>
                  *We'll keep your number hidden for privacy
                </Typography>
                <RadioGroup
                  aria-label="receive calls"
                  name="calls_status"
                  value={willAcceptCalls}
                  onChange={(e) => setWillAcceptCalls(e.target.value)}>
                  <BidmiiRadio
                    state={willAcceptCalls}
                    value="true"
                    label="Accept calls"
                    disabled={imagesRequired}
                  />
                  <BidmiiRadio
                    state={willAcceptCalls}
                    value="false"
                    label="No calls"
                  />
                </RadioGroup>
              </div>
            </Stack>
          </form>

          {saveType === 'publish' && (
            <Box my={4}>
              <Box
                className={classes.promotionBox}
                style={{
                  backgroundColor:
                    promotionType === 'free'
                      ? theme.palette.blue.light
                      : theme.palette.secondary.main,
                  borderColor:
                    promotionType === 'free'
                      ? theme.palette.blue.dark
                      : theme.palette.gray.border,
                }}>
                <Grid container alignItems="center">
                  <BidmiiCheckbox
                    checked={promotionType === 'free'}
                    onChange={(e: any) => {
                      setPromotionType('free');
                      const scrollDiv: any =
                        document.getElementById('promotionScrollDiv');
                      if (scrollDiv) {
                        scrollDiv.scrollTop = scrollDiv?.scrollHeight;
                      }
                    }}
                  />
                  <Typography variant="subtitle1" style={{fontSize: 18}}>
                    No Promotion (FREE)
                  </Typography>
                </Grid>
                <Typography variant="subtitle2" fontSize={13} marginLeft={4}>
                  Publish your project without any promotion.
                </Typography>
              </Box>

              <Box
                className={classes.promotionBox}
                style={{
                  backgroundColor:
                    promotionType === 'promoted'
                      ? theme.palette.blue.light
                      : theme.palette.secondary.main,
                  borderColor:
                    promotionType === 'promoted'
                      ? theme.palette.blue.dark
                      : theme.palette.gray.border,
                }}>
                <Grid container alignItems="center">
                  <BidmiiCheckbox
                    checked={promotionType === 'promoted'}
                    onChange={(e: any) => {
                      setPromotionType('promoted');
                      const scrollDiv: any =
                        document.getElementById('promotionScrollDiv');
                      if (scrollDiv) {
                        scrollDiv.scrollTop = scrollDiv?.scrollHeight;
                      }
                    }}
                  />
                  <Typography variant="subtitle1" style={{fontSize: 18}}>
                    Promoted Project ($14.99)
                  </Typography>
                </Grid>
                <Typography variant="subtitle2" fontSize={13} marginLeft={4}>
                  For 7 days, contractors see your project at the top of the
                  jobs list.
                </Typography>
              </Box>
            </Box>
          )}

          <ActionButtons
            secondaryAction={() => changeStepHandler(-1)}
            primaryAction={handleFinish}
            primaryLabel={
              saveType === 'publish' ? 'PUBLISH PROJECT' : 'SAVE AS DRAFT'
            }
            disabled={submitDisabled}
            loading={loading}
          />
        </div>
      </Slide>

      {promotionDialogOpen && (
        <PromoteProjectDialog
          open
          requestForProposal={project}
          handleClose={() => setPromotionDialogOpen(false)}
        />
      )}
    </>
  );
};

export default PublishAndPromoteStep;
